import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Shield, RefreshCcw, ChevronRight, CheckCircle } from 'lucide-react';

const Policies = () => {
  const [activeTab, setActiveTab] = useState('terms');
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const tabs = [
    { id: 'terms', label: 'Terms & Conditions', icon: FileText },
    { id: 'privacy', label: 'Privacy Policy', icon: Shield },
    { id: 'refund', label: 'Refund Policy', icon: RefreshCcw },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'terms':
        return (
          <div className="space-y-6">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Terms & Conditions</h2>
              <p className="text-gray-600 mb-4">
                Welcome to NEP (NextGen Exam Platform). By accessing and using our platform, you agree to comply with and be bound by the following terms and conditions.
              </p>
              <p className="text-sm text-gray-500 mb-6">Last updated: April 2026</p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">1. Acceptance of Terms</h3>
              <p className="text-gray-600 mb-3">
                By accessing or using the NextGen Exam Platform ("the Platform"), operated by Qbyte Technology Pty Ltd ("the Company", "we", "us"), you agree to be bound by these Terms and Conditions and our Privacy Policy. These terms apply to all visitors, users, and others who access or use the Service.
              </p>
              <p className="text-gray-600 mb-3">
                By registering for an account, purchasing exam packages, or using any part of our service, you acknowledge that you have read, understood, and agree to be bound by these Terms & Conditions. If you do not agree with any part of these terms, you must not use our platform.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">2. User Accounts</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>You must provide accurate and complete information during registration</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>You are responsible for maintaining the confidentiality of your account credentials</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>You must notify us immediately of any unauthorized access to your account</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>One account is limited to one user; sharing accounts is prohibited</span>
                </li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">3. Exam Packages & Purchases</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>All purchases are subject to availability and our confirmation</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>All purchased exam content is accessible for a period of 1 year from the date of purchase. After this period, access will expire and cannot be restored unless repurchased.</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Once an exam is started, it must be completed within the allotted time</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>We reserve the right to modify exam content and pricing with notice</span>
                </li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">4. Intellectual Property & AI Disclosure</h3>
              <p className="text-gray-600 mb-3">
                The Platform and its original content are the exclusive property of Qbyte Technology Pty Ltd. Content may include AI-generated material that has been reviewed and curated. We do not claim exclusive copyright over all AI-generated elements; however, the structure, arrangement, and presentation are proprietary.
              </p>
              <p className="text-gray-600 mb-3">
                All content on the NEP platform, including exam questions, explanations, graphics, and software, is the property of Qbyte Technology Pty Ltd and is protected by copyright and other intellectual property laws. Users may not reproduce, distribute, or create derivative works without our express permission.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">5. Limited Licence</h3>
              <p className="text-gray-600 mb-3">
                Users are granted a limited licence for personal educational use only. This licence is non-transferable and may be revoked by us at any time if these terms are breached.
              </p>
            </section>

            <section className="bg-rose-50 border-2 border-rose-200 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-rose-900 mb-3">6. Content Reporting & Integrity</h3>
              <p className="text-rose-800 mb-3">
                <strong className="text-rose-950">Mandatory Reporting:</strong> If anyone finds any duplicate or inappropriate content, they must inform us immediately via our <Link to="/contact" className="text-rose-600 hover:text-rose-800 underline font-medium">Contact Us</Link> page. We take content accuracy and originality seriously and will investigate all reports.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">7. Prohibited Conduct</h3>
              <p className="text-gray-600 mb-3">Users are prohibited from:</p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">•</span>
                  <span>Sharing exam content or answers with others</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">•</span>
                  <span>Using automated tools or bots to access the platform</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">•</span>
                  <span>Attempting to circumvent our security measures</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">•</span>
                  <span>Using the platform for any illegal purpose</span>
                </li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">8. Limitation of Liability</h3>
              <p className="text-gray-600 mb-3">
                NEP shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use or inability to use the service. This includes but is not limited to damages for loss of profits, goodwill, use, data, or other intangible losses.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">9. Termination</h3>
              <p className="text-gray-600 mb-3">
                We reserve the right to terminate or suspend your account immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms & Conditions.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">10. Changes to Terms</h3>
              <p className="text-gray-600 mb-3">
                We may update these Terms & Conditions from time to time. We will notify you of any changes by posting the new terms on this page and updating the "Last updated" date. Your continued use of the platform after any changes constitutes acceptance of the new terms.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">11. Governing Law</h3>
              <p className="text-gray-600 mb-3">
                These Terms & Conditions shall be governed by and construed in accordance with the laws of Australia, without regard to its conflict of law provisions.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">12. Contact Information</h3>
              <p className="text-gray-600 mb-3">
                If you have any questions about these Terms & Conditions, please contact us through our <Link to="/contact" className="text-blue-600 hover:underline">Contact Us</Link> page.
              </p>
            </section>
          </div>
        );

      case 'privacy':
        return (
          <div className="space-y-6">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Privacy Policy</h2>
              <p className="text-gray-600 mb-4">
                At NEP, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.
              </p>
              <p className="text-sm text-gray-500 mb-6">Last updated: April 2026</p>
            </section>

            <section className="bg-emerald-50 border border-emerald-200 p-4 rounded-lg">
              <p className="text-emerald-800">
                We are committed to protecting your privacy in accordance with the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs). We collect only mandatory information necessary to provide our services, such as your name, address, mobile number and email address.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">1. Information We Collect</h3>
              <p className="text-gray-600 mb-3 font-medium">Personal Information:</p>
              <ul className="space-y-2 text-gray-600 mb-4">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Name, email address, and contact details</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Account login credentials (encrypted)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Student grade level and educational information</span>
                </li>
              </ul>
              
              <p className="text-gray-600 mb-3 font-medium">Usage Information:</p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Exam participation data and performance metrics</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Device and browser information</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>IP address and access timestamps</span>
                </li>
              </ul>
            </section>

            <section className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">2. Payment Information</h3>
              <p className="text-gray-700 mb-3">
                <strong>Important:</strong> We do not collect, process, or store your payment card details (Credit/Debit card numbers, CVV, etc.). All financial transactions are handled through secure, third-party payment gateways. We only receive confirmation of payment status.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">3. How We Use Your Information</h3>
              <p className="text-gray-600 mb-3">We use the information we collect to:</p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Provide, maintain, and improve our services</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Process your purchases and manage your account</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Generate performance reports and track progress</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Communicate with you about updates, offers, and support</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Prevent fraud and ensure platform security</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Comply with legal obligations</span>
                </li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">4. Information Sharing</h3>
              <p className="text-gray-600 mb-3">
                We do not sell your personal information to third parties. We may share your information in the following circumstances:
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-gray-400 mr-2">•</span>
                  <span><strong>Service Providers:</strong> With trusted third parties who assist in operating our platform (e.g., payment processors, hosting providers)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-400 mr-2">•</span>
                  <span><strong>Legal Requirements:</strong> When required by law, court order, or governmental regulation</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-400 mr-2">•</span>
                  <span><strong>Protection of Rights:</strong> To protect our rights, privacy, safety, or property</span>
                </li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">5. Data Security</h3>
              <p className="text-gray-600 mb-3">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes:
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <Shield className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>SSL/TLS encryption for data transmission</span>
                </li>
                <li className="flex items-start">
                  <Shield className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Secure password hashing and storage</span>
                </li>
                <li className="flex items-start">
                  <Shield className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Regular security assessments and monitoring</span>
                </li>
                <li className="flex items-start">
                  <Shield className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Limited access to personal information by authorized personnel only</span>
                </li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">6. Your Rights</h3>
              <p className="text-gray-600 mb-3">You have the following rights regarding your personal information:</p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span><strong>Access:</strong> Request a copy of your personal data</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span><strong>Correction:</strong> Update or correct inaccurate information</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span><strong>Deletion:</strong> Request deletion of your account and data</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span><strong>Portability:</strong> Request transfer of your data</span>
                </li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">7. Cookies & Tracking</h3>
              <p className="text-gray-600 mb-3">
                We use cookies and similar technologies to enhance your experience, analyze usage patterns, and deliver personalized content. You can control cookie settings through your browser preferences.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">8. Children's Privacy</h3>
              <p className="text-gray-600 mb-3">
                Our platform is designed for students and requires parental consent for users under 13 years of age. We do not knowingly collect personal information from children without appropriate parental consent.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">9. Changes to Privacy Policy</h3>
              <p className="text-gray-600 mb-3">
                We may update this Privacy Policy periodically. We will notify you of significant changes via email or through a notice on our platform. Your continued use after changes indicates acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">10. Contact Us</h3>
              <p className="text-gray-600 mb-3">
                If you have questions or concerns about this Privacy Policy or our data practices, please contact us through our <Link to="/contact" className="text-blue-600 hover:underline">Contact Us</Link> page.
              </p>
            </section>
          </div>
        );

      case 'refund':
        return (
          <div className="space-y-6">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Refund Policy</h2>
              <p className="text-gray-600 mb-4">
                At NEP, we strive to provide high-quality educational content and services. This Refund Policy outlines the conditions under which refunds may be granted for purchases made on our platform.
              </p>
              <p className="text-sm text-gray-500 mb-6">Last updated: April 2026</p>
            </section>

            <section className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Overview</h3>
              <p className="text-blue-800">
                We offer a 14-day money-back guarantee for most exam packages. Refund requests will be evaluated based on usage and eligibility criteria outlined below.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">1. Eligible for Full Refund</h3>
              <p className="text-gray-600 mb-3">
                We offer refunds on digital exam sets under specific conditions to maintain the integrity of our assessment materials.
              </p>
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-3">
                <p className="text-gray-600">
                  <strong>Strict Usage Limit:</strong> A refund request will only be considered if the user has not accessed or used more than one (1) exam from the entire exam set purchased. Once a second exam within a set is opened or attempted, the user is deemed to have consumed the service, and no refund will be issued.
                </p>
              </div>
              <p className="text-gray-600 mb-3">You are eligible for a full refund if:</p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>You request a refund within 7 days of purchase</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>You have not accessed more than one (1) exam from the entire exam set purchased.</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Duplicate purchase occurred</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Technical issues prevented you from accessing the purchased content</span>
                </li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">2. Non-Refundable Circumstances</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">•</span>
                  <span>Change of mind after accessing more than one exam.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">•</span>
                  <span>Incompatibility with user hardware/software where requirements were clearly stated.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">•</span>
                  <span>Accounts terminated due to breaches of our Terms and Conditions.</span>
                </li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">3. Refund Process</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-blue-600 font-bold text-sm">1</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Submit Request</p>
                    <p className="text-gray-600 text-sm">User has to login to the system and raise a support ticket through help and feedback form</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-blue-600 font-bold text-sm">2</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Review</p>
                    <p className="text-gray-600 text-sm">Our team will review your request within 3-5 business days</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-blue-600 font-bold text-sm">3</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Decision</p>
                    <p className="text-gray-600 text-sm">You will receive an email with our decision and refund details if approved</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-blue-600 font-bold text-sm">4</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Refund</p>
                    <p className="text-gray-600 text-sm">Approved refunds are processed within 5-10 business days to the original payment method</p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">4. Refund Methods</h3>
              <p className="text-gray-600 mb-3">
                Refunds will be issued to the original payment method used for the purchase. Refunds are typically processed within 7-14 business days, depending on your payment provider.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">5. Special Circumstances</h3>
              <div className="space-y-3">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="font-medium text-gray-800">Technical Issues</p>
                  <p className="text-gray-600 text-sm">If you experience technical problems preventing access to exams, please contact support immediately. We will work to resolve the issue or provide a refund if we cannot fix it.</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="font-medium text-gray-800">Duplicate Charges</p>
                  <p className="text-gray-600 text-sm">If you notice duplicate charges on your account, we will automatically refund the duplicate amount once verified.</p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">6. Contact for Refunds</h3>
              <p className="text-gray-600 mb-3">
                To inquire about refund eligibility, please:
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <ChevronRight className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Visit our <Link to="/contact" className="text-blue-600 hover:underline">Contact Us</Link> page</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Include your order number and registered email address</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Provide a clear explanation of your refund request</span>
                </li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">7. Policy Changes</h3>
              <p className="text-gray-600 mb-3">
                We reserve the right to modify this Refund Policy at any time. Changes will be effective immediately upon posting to the platform. Refund requests submitted before policy changes will be evaluated under the terms in effect at the time of submission.
              </p>
            </section>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Logo */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md py-2' : 'bg-transparent py-4'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="/logo/nep-logo.png" 
                alt="NEP Logo" 
                className="h-10 w-auto"
              />
              <span className="text-xl font-bold text-gray-900 hidden sm:block">NEP</span>
            </Link>
            <Link to="/" className="text-gray-600 hover:text-gray-900 transition-colors">
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-12 bg-gradient-to-br from-emerald-600 to-green-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Legal & Policies</h1>
          <p className="text-xl text-emerald-100 max-w-2xl mx-auto">
            Transparency and trust are important to us. Review our terms, privacy practices, and refund policies.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-full font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-emerald-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100 shadow'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          {renderContent()}
        </div>

        {/* Contact CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">
            Have questions about our policies? We're here to help.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-full font-medium transition-colors"
          >
            <span>Contact Us</span>
            <ChevronRight className="h-5 w-5" />
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <img 
                src="/logo/nep-logo.png" 
                alt="NEP Logo" 
                className="h-8 w-auto"
              />
              <span className="text-white font-semibold">NEP</span>
            </div>
            <div className="flex space-x-6 text-sm">
              <Link to="/policies" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/policies" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/policies" className="hover:text-white transition-colors">Refunds</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            </div>
          </div>
          <div className="mt-6 text-center text-sm">
            &copy; {new Date().getFullYear()} NEP - NextGen Exam Platform. All rights reserved.
          </div>
          <div className="mt-2 text-center text-xs text-gray-500">
            QBYTE Technology Pty Ltd | ABN: 78697357510 | ACN: 697 357 510
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Policies;
